import { useEffect, useState } from "react";
import { CalendarRange, Loader2, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { SectionLabel } from "@/components/Brand";
import { MultiDatePicker, shortDay } from "@/components/MultiDatePicker";
import { HelpersPicker } from "@/components/HelpersPicker";
import { Textarea } from "@/components/ui/textarea";
import { DailyReportSwitch } from "@/components/DailyReportSwitch";
import { LunchFields } from "@/components/LunchFields";
import { recurLabel, RecurringFields } from "@/components/RecurringFields";
import { AwayBox } from "@/components/AwayBox";

function EditRecurring({ job, onClose, onSaved }) {
  const r = job.site.recurring;
  const [v, setV] = useState({ weekdays: r.weekdays, shifts: r.shifts, day_shifts: r.day_shifts || {}, start_date: r.start_date,
    closed_dates: r.closed_dates || [], skip_holidays: r.skip_holidays !== false });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!v.weekdays.length) return toast.error("Scegli almeno un giorno della settimana");
    setBusy(true);
    try {
      await api.put(`/admin/cantieri/${job.series_id}/recurring`, { weekdays: v.weekdays, shifts: v.shifts, day_shifts: v.day_shifts, closed_dates: v.closed_dates, skip_holidays: v.skip_holidays });
      toast.success("Orario aggiornato: i turni da domani seguono il nuovo orario");
      onSaved();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-lg overflow-y-auto" data-testid="edit-recurring-dialog">
        <DialogHeader><DialogTitle>Modifica orario ripetitivo</DialogTitle></DialogHeader>
        <RecurringFields value={v} onChange={setV} hideStart />
        <p className="text-xs text-slate-500">Oggi e i giorni passati non cambiano. I turni futuri non più previsti vengono tolti, quelli nuovi creati con la stessa squadra.</p>
        <Button onClick={save} disabled={busy} className="h-11 w-full rounded-full" data-testid="edit-recurring-save">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Salva orario</Button>
      </DialogContent>
    </Dialog>
  );
}

function ShiftDialog({ job, mode, onClose, onSaved }) {
  const [s, setS] = useState({ date: mode === "add" ? job.date : job.date, start: mode === "add" ? "14:00" : job.start, end: mode === "add" ? "18:00" : job.end,
    technician_id: job.technician_id || "", helper_ids: (job.helpers || []).map((h) => h.id) });
  const [techs, setTechs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [conf, setConf] = useState(null);
  useEffect(() => {
    Promise.all([api.get("/admin/technicians"), api.get("/admin/technicians", { params: { role: "responsabile" } })])
      .then(([a, b]) => setTechs([...a.data, ...b.data].filter((t) => t.active)));
  }, []);
  const save = async () => {
    setBusy(true); setConf(null);
    const body = { ...s, technician_id: s.technician_id || null, helper_ids: s.technician_id ? s.helper_ids.filter((h) => h !== s.technician_id) : [] };
    try {
      if (mode === "add") await api.post(`/admin/series/${job.series_id}/shifts`, body); else await api.put(`/admin/shifts/${job.id}`, body);
      toast.success(mode === "add" ? "Turno aggiunto" : "Turno aggiornato");
      onSaved();
    } catch (e) {
      if (e?.response?.status === 409) setConf(e.response.data.detail); else toast.error(errMsg(e));
    } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="shift-dialog">
        <DialogHeader><DialogTitle>{mode === "add" ? "Aggiungi turno" : `Turno del ${shortDay(job.date)}`}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-3 gap-3">
          {mode === "add" && <div className="col-span-3 space-y-1"><Label>Giorno</Label><Input type="date" value={s.date} onChange={(e) => setS((p) => ({ ...p, date: e.target.value }))} data-testid="shift-date-input" /></div>}
          <div className="space-y-1"><Label>Dalle</Label><Input type="time" value={s.start} onChange={(e) => setS((p) => ({ ...p, start: e.target.value }))} data-testid="shift-dialog-start" /></div>
          <div className="space-y-1"><Label>Alle</Label><Input type="time" value={s.end} onChange={(e) => setS((p) => ({ ...p, end: e.target.value }))} data-testid="shift-dialog-end" /></div>
        </div>
        <div className="space-y-1"><Label>Responsabile del turno</Label>
          <select value={s.technician_id} onChange={(e) => setS((p) => ({ ...p, technician_id: e.target.value }))} className="w-full rounded-lg border bg-white px-3 py-2 text-sm" data-testid="shift-lead-select">
            <option value="">Nessuno</option>
            {techs.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select></div>
        <HelpersPicker techs={techs} leadId={s.technician_id} value={s.helper_ids} onChange={(helper_ids) => setS((p) => ({ ...p, helper_ids }))} />
        {conf && <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700" data-testid="shift-conflict-alert"><p className="font-semibold">{conf.message}</p><ul className="mt-1 list-disc pl-5">{conf.conflicts?.map((c, i) => <li key={i}>{c.message}</li>)}</ul></div>}
        <Button onClick={save} disabled={busy} className="rounded-full" data-testid="shift-save-button">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Salva turno</Button>
      </DialogContent>
    </Dialog>
  );
}

function AssignDays({ job, onClose, onSaved }) {
  const [dates, setDates] = useState([]);
  const [wins, setWins] = useState([{ start: job.start, end: job.end }]);
  const [lead, setLead] = useState(job.technician_id || "");
  const [helpers, setHelpers] = useState((job.helpers || []).map((h) => h.id));
  const [plan, setPlan] = useState("");
  const [techs, setTechs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [conf, setConf] = useState(null);
  useEffect(() => {
    Promise.all([api.get("/admin/technicians"), api.get("/admin/technicians", { params: { role: "responsabile" } })])
      .then(([a, b]) => setTechs([...a.data, ...b.data].filter((t) => t.active)));
  }, []);
  const updW = (i, k, v) => setWins((p) => p.map((w, j) => (j === i ? { ...w, [k]: v } : w)));
  const save = async () => {
    if (!dates.length) return toast.error("Seleziona almeno un giorno");
    setBusy(true); setConf(null);
    try {
      const { data } = await api.post(`/admin/series/${job.series_id}/assign`, { dates, windows: wins, technician_id: lead || null,
        helper_ids: lead ? helpers.filter((h) => h !== lead) : [], work_plan: plan });
      toast.success(`Assegnati ${dates.length} giorni (${data.length} appuntamenti)`);
      onSaved();
    } catch (e) {
      if (e?.response?.status === 409) setConf(e.response.data.detail); else toast.error(errMsg(e));
    } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto" data-testid="assign-days-dialog">
        <DialogHeader><DialogTitle>Assegna nuovi giorni e orari</DialogTitle></DialogHeader>
        <div className="space-y-1"><Label>Giorni</Label><MultiDatePicker value={dates} onChange={(d) => { setDates(d); setConf(null); }} noPast hint="Tocca tutti i giorni di questa assegnazione." /></div>
        <div className="space-y-2"><Label>Orari (valgono per ogni giorno scelto)</Label>
          {wins.map((w, i) => (
            <div key={i} className="flex items-center gap-2" data-testid={`assign-window-${i}`}>
              <Input type="time" value={w.start} onChange={(e) => updW(i, "start", e.target.value)} className="w-28" data-testid={`assign-start-${i}`} />
              <span className="text-slate-400">–</span>
              <Input type="time" value={w.end} onChange={(e) => updW(i, "end", e.target.value)} className="w-28" data-testid={`assign-end-${i}`} />
              {wins.length > 1 && <Button type="button" size="icon" variant="ghost" onClick={() => setWins((p) => p.filter((_, j) => j !== i))} data-testid={`assign-remove-${i}`}><Trash2 className="h-4 w-4 text-red-600" /></Button>}
            </div>
          ))}
          {wins.length < 6 && <Button type="button" size="sm" variant="ghost" className="-ml-2" onClick={() => setWins((p) => [...p, { start: "14:00", end: "18:00" }])} data-testid="assign-add-window"><Plus className="h-4 w-4" /> Aggiungi orario</Button>}
        </div>
        <div className="space-y-1"><Label>Responsabile</Label>
          <select value={lead} onChange={(e) => setLead(e.target.value)} className="w-full rounded-lg border bg-white px-3 py-2 text-sm" data-testid="assign-lead-select">
            <option value="">Nessuno (da assegnare)</option>
            {techs.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select></div>
        <HelpersPicker techs={techs} leadId={lead} value={helpers} onChange={setHelpers} />
        <div className="space-y-1"><Label>Programma di lavoro</Label>
          <Textarea rows={5} value={plan} onChange={(e) => setPlan(e.target.value)} maxLength={4000} placeholder="Cosa bisogna fare in questi giorni: lavorazioni, materiali, priorità…" data-testid="assign-work-plan" />
          <p className="text-xs text-slate-500">Il programma vale solo per i giorni di questa assegnazione. La squadra lo vede nella scheda di ogni giorno.</p></div>
        {conf && <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700" data-testid="assign-conflict-alert"><p className="font-semibold">{conf.message}</p><ul className="mt-1 list-disc pl-5">{conf.conflicts?.map((c, i) => <li key={i}>{c.message}</li>)}</ul></div>}
        <Button onClick={save} disabled={busy} className="rounded-full" data-testid="assign-save-button">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Assegna{dates.length ? ` ${dates.length} giorni` : ""}</Button>
      </DialogContent>
    </Dialog>
  );
}

function EditInfo({ job, onClose, onSaved }) {
  const s = job.site || {};
  const [f, setF] = useState({ name: s.name || job.client.last_name, committente: s.committente || "", referente_phone: s.referente_phone || "",
    address: job.client.address, city: job.client.city, notes: job.notes || "", daily_report: s.daily_report !== false, lunch_start: s.lunch_start || null, lunch_end: s.lunch_end || null,
    lat: job.location?.coordinates?.[1] ?? null, lng: job.location?.coordinates?.[0] ?? null });
  const [busy, setBusy] = useState(false);
  const upd = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const save = async () => {
    setBusy(true);
    try { await api.put(`/admin/cantieri/${job.series_id}/info`, f); toast.success("Dati del cantiere aggiornati"); onSaved(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="cantiere-info-dialog">
        <DialogHeader><DialogTitle>Dati del cantiere</DialogTitle></DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1 sm:col-span-2"><Label>Nome cantiere</Label><Input value={f.name} onChange={upd("name")} data-testid="cantiere-info-name" /></div>
          <div className="space-y-1"><Label>Committente</Label><Input value={f.committente} onChange={upd("committente")} data-testid="cantiere-info-committente" /></div>
          <div className="space-y-1"><Label>Telefono referente</Label><Input value={f.referente_phone} onChange={upd("referente_phone")} data-testid="cantiere-info-phone" /></div>
          <div className="space-y-1"><Label>Indirizzo</Label><Input value={f.address} onChange={upd("address")} data-testid="cantiere-info-address" /></div>
          <div className="space-y-1"><Label>Comune</Label><Input value={f.city} onChange={upd("city")} data-testid="cantiere-info-city" /></div>
          <div className="space-y-1 sm:col-span-2"><Label>Note</Label><Textarea rows={3} value={f.notes} onChange={upd("notes")} data-testid="cantiere-info-notes" /></div>
          <DailyReportSwitch checked={f.daily_report} onChange={(v) => setF((p) => ({ ...p, daily_report: v }))} testid="cantiere-info-daily-report-switch" />
          <LunchFields start={f.lunch_start} end={f.lunch_end} onChange={(v) => setF((p) => ({ ...p, ...v }))} testid="cantiere-info-lunch" className="sm:col-span-2" />
        </div>
        <Button onClick={save} disabled={busy} className="rounded-full" data-testid="cantiere-info-save">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Salva</Button>
      </DialogContent>
    </Dialog>
  );
}

function EditDays({ job, open, onClose, onSaved }) {
  const [dates, setDates] = useState(job.series_dates);
  const [t, setT] = useState({ start: job.start, end: job.end });
  const [busy, setBusy] = useState(false);
  const [conf, setConf] = useState(null);
  const [locked, setLocked] = useState([]);
  const [techs, setTechs] = useState([]);
  const [helpers, setHelpers] = useState((job.helpers || []).map((h) => h.id));
  useEffect(() => {
    api.get(`/jobs/${job.id}/series`).then((r) => setLocked(r.data.filter((d) => d.status === "completato").map((d) => d.date)));
    api.get("/admin/technicians").then((r) => setTechs(r.data.filter((t) => t.active)));
  }, [job.id]);
  const save = async () => {
    if (!dates.length) return toast.error("Seleziona almeno un giorno");
    setBusy(true); setConf(null);
    try {
      await api.put(`/admin/series/${job.series_id}`, { dates, ...t, job_id: job.id, ...(job.technician_id ? { helper_ids: helpers } : {}) });
      toast.success("Giorni del cantiere aggiornati");
      onSaved();
    } catch (e) {
      if (e?.response?.status === 409) setConf(e.response.data.detail); else toast.error(errMsg(e));
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="series-edit-dialog">
        <DialogHeader><DialogTitle>Giorni e squadra del cantiere</DialogTitle></DialogHeader>
        <MultiDatePicker value={dates} onChange={setDates} locked={locked} />
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1"><Label>Orario per i nuovi giorni, dalle</Label><Input type="time" value={t.start} onChange={(e) => setT((p) => ({ ...p, start: e.target.value }))} data-testid="series-start-input" /></div>
          <div className="space-y-1"><Label>Alle</Label><Input type="time" value={t.end} onChange={(e) => setT((p) => ({ ...p, end: e.target.value }))} data-testid="series-end-input" /></div>
        </div>
        {job.technician_id && <HelpersPicker techs={techs} leadId={job.technician_id} value={helpers} onChange={setHelpers} />}
        {conf && <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700" data-testid="series-conflict-alert"><p className="font-semibold">{conf.message}</p><ul className="mt-1 list-disc pl-5">{conf.conflicts?.map((c, i) => <li key={i}>{c.message}</li>)}</ul></div>}
        <Button onClick={save} disabled={busy} className="rounded-full" data-testid="series-save-button">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Salva giorni</Button>
      </DialogContent>
    </Dialog>
  );
}

export function SeriesBox({ job, onChanged }) {
  const [edit, setEdit] = useState(false);
  const [info, setInfo] = useState(false);
  const [shift, setShift] = useState(null);
  const [assign, setAssign] = useState(false);
  const [recEdit, setRecEdit] = useState(false);
  if (!job.series_id) return null;
  const editable = !["rifiutato", "annullato"].includes(job.status) && job.series_index !== undefined && !(job.status === "completato" && job.series_index === job.series_total);
  const rec = job.site?.recurring;
  const stop = async () => {
    if (!window.confirm("Terminare l'orario ripetitivo? Le giornate future verranno annullate e oggi diventa l'ultimo giorno.")) return;
    try { await api.post(`/admin/cantieri/${job.series_id}/stop`); toast.success("Orario ripetitivo terminato"); onChanged(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const cancel = async () => {
    if (!window.confirm("Annullare tutte le giornate non ancora svolte di questo cantiere?")) return;
    try { await api.post(`/admin/series/${job.series_id}/cancel`); toast.success("Cantiere annullato"); onChanged(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-3 rounded-2xl border border-[#cfe3b5] bg-[#f6faf0] p-4" data-testid="series-box">
      <SectionLabel>{rec ? `Cantiere ripetitivo${rec.stopped ? ` · terminato il ${shortDay(rec.stopped)}` : ""}` : `Cantiere · giorno ${job.series_index} di ${job.series_total}`}</SectionLabel>
      {rec && <p className="text-sm font-medium" data-testid="series-recurring-label">{recurLabel(rec)}</p>}
      <div className="flex flex-wrap gap-1.5">
        {[...new Set(job.series_dates)].filter((d) => !rec || d >= job.date).slice(0, rec ? 14 : undefined).map((d, i) => (
          <span key={d} className={`rounded-full px-2.5 py-1 text-xs font-medium capitalize ${d === job.date ? "bg-[#61a229] text-white" : "bg-white text-slate-700 border"}`} data-testid={`series-day-${i}`}>{shortDay(d)}</span>
        ))}
      </div>
      {editable && (
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" className="rounded-full" onClick={() => setEdit(true)} data-testid="series-edit-button"><CalendarRange className="h-4 w-4" /> Modifica giorni e squadra</Button>
          {job.site && <Button size="sm" variant="outline" className="rounded-full" onClick={() => setInfo(true)} data-testid="cantiere-info-button">Modifica dati cantiere</Button>}
          {job.site && !rec && ["in_attesa", "confermato"].includes(job.status) && <Button size="sm" variant="outline" className="rounded-full" onClick={() => setShift("edit")} data-testid="shift-edit-button">Modifica questo giorno</Button>}
          {job.site && !rec && <Button size="sm" className="rounded-full bg-[#61a229] hover:bg-[#4f8a1f]" onClick={() => setAssign(true)} data-testid="assign-days-button"><Plus className="h-4 w-4" /> Assegna nuovi giorni e orari</Button>}
          {rec && !rec.stopped && <Button size="sm" variant="outline" className="rounded-full" onClick={() => setRecEdit(true)} data-testid="series-edit-recurring-button">Modifica orario ripetitivo</Button>}
          {rec && !rec.stopped && <Button size="sm" variant="outline" className="rounded-full text-red-600" onClick={stop} data-testid="series-stop-recurring-button">Termina orario ripetitivo</Button>}
          {!rec && <Button size="sm" variant="outline" className="rounded-full text-red-600" onClick={cancel} data-testid="series-cancel-button">Annulla cantiere</Button>}
        </div>
      )}
      {edit && <EditDays job={job} open={edit} onClose={() => setEdit(false)} onSaved={() => { setEdit(false); onChanged(); }} />}
      {recEdit && <EditRecurring job={job} onClose={() => setRecEdit(false)} onSaved={() => { setRecEdit(false); onChanged(); }} />}
      <AwayBox away={job.away} />
      {info && <EditInfo job={job} onClose={() => setInfo(false)} onSaved={() => { setInfo(false); onChanged(); }} />}
      {shift && <ShiftDialog job={job} mode={shift} onClose={() => setShift(null)} onSaved={() => { setShift(null); onChanged(); }} />}
      {assign && <AssignDays job={job} onClose={() => setAssign(false)} onSaved={() => { setAssign(false); onChanged(); }} />}
    </div>
  );
}
